import { defaultReturnTo, startGoogleLogin, useAuth } from '@redline/shared/auth';
import { isDevOrigin } from '@redline/shared/env';
import { useEffect, useState } from 'react';
import { BoardPage } from './BoardPage';
import { api, restBoardsApi } from './boardsApi';
import { Dashboard } from './Dashboard';
import { ShareHost } from './drive';
import { navigate, useRoute } from './router';
import { Toasts } from './ui';

const boards = restBoardsApi(api);

export function App() {
  const auth = useAuth(api);
  const route = useRoute();

  if (auth.status === 'loading') return <div className="xb-center xb-muted">Loading…</div>;
  if (auth.status === 'signed_out' || !auth.session) return <Landing error={auth.error} />;
  const user = auth.session.user;

  return (
    <>
      {route.name === 'home' && <Dashboard boards={boards} user={user} onSignOut={auth.signOut} />}
      {route.name === 'board' && <BoardPage key={route.id} boardId={route.id} boards={boards} user={user} />}
      {route.name === 'join' && <Join token={route.token} />}
      {route.name === 'notfound' && (
        <div className="xb-center">
          <p>Page not found.</p>
          <button className="xb-btn" onClick={() => navigate('/')}>
            Go to your boards
          </button>
        </div>
      )}
      <ShareHost />
      <Toasts />
    </>
  );
}

function Landing({ error }: { error: string | null }) {
  // Come back to the same page (e.g. /join/<token>) after Google sign-in.
  const returnTo = isDevOrigin() ? `${location.origin}${location.pathname}` : location.pathname || defaultReturnTo();
  return (
    <div className="xb-landing">
      <div className="xb-landing-card">
        <div className="xb-logo" aria-hidden>
          <span style={{ background: '#fff9b1' }} />
          <span style={{ background: '#a6ccf5' }} />
          <span style={{ background: '#ffcee0' }} />
          <span style={{ background: '#d5f692' }} />
        </div>
        <h1>Excalidraw Board</h1>
        <p className="xb-muted">A collaborative whiteboard — sticky notes, shapes, connectors and frames, live with your team.</p>
        {error && <p className="xb-error">{error}</p>}
        <button className="xb-btn xb-btn-primary xb-btn-lg" onClick={() => startGoogleLogin(returnTo, api)}>
          <GoogleG /> Sign in with Google
        </button>
        <p className="xb-small xb-muted">Eos Lightmedia · whiteboard evaluation</p>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function Join({ token }: { token: string }) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    boards
      .join(token)
      .then((b) => !cancelled && navigate(`/board/${b.id}`, { replace: true }))
      .catch((e: Error) => !cancelled && setError(e.message || 'This invite link is invalid or has expired.'));
    return () => {
      cancelled = true;
    };
  }, [token]);
  return (
    <div className="xb-center">
      {error ? (
        <>
          <p className="xb-error">Could not join the board: {error}</p>
          <button className="xb-btn" onClick={() => navigate('/')}>
            Go to your boards
          </button>
        </>
      ) : (
        <p className="xb-muted">Joining board…</p>
      )}
    </div>
  );
}
